import React, { useEffect, useState } from 'react'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { isReactNativeApp, sendToNative } from '@mobi/web-native-comms/web'
import { getPointDistance, prettifyDistance } from '@core/Areas/VenueMode/Utils/geoFenceUtils'
import {
  OverviewStyled,
  StatisticsStyled,
  StatisticsRowStyled,
  TableStyled,
  RowStyled,
  CellIndexStyled,
  CellLatLonStyled,
  CellAccuracyStyled,
  CellDeltaStyled,
  CellMockedStyled,
  CellPreciseAccuracyStyled,
  CellTimeStyled,
  CellInsideStyled,
  CellDistanceStyled,
  CellVenueLocationStyled,
  CellVenueNameStyled,
  CellErrorStyled,
  DiagnosticsContainerStyled,
  ButtonContainerStyled,
} from './Diagnostics.styles'
import { VenueModeButton } from '@core/Areas/VenueMode/Components/Shared/VenueModeButton'
import { getCoords, GeoLocation } from '../../Store'
import {
  validateCoordinates,
  ValidateCoordinatesEnum,
} from '@core/Areas/VenueMode/Utils/validateCoordinates'

const MAX_VISIBLE_LOCATION_UPDATES = 100
const MAX_LOCATION_UPDATES = 1000

interface DiagnosticsRow extends GeoLocation {
  error?: string
  delta?: number
}

export const Diagnostics: React.FC = () => {
  const currentCoords = useSelector(getCoords)

  const [lastKnownLocations, setLastKnownLocations] = useState<DiagnosticsRow[]>([])
  const [numberOfUpdates, setNumberOfUpdates] = useState(0)
  const [numberOfErrors, setNumberOfErrors] = useState(0)
  const [numberOfInside, setNumberOfInside] = useState(0)

  useEffect(() => {
    if (!currentCoords) return

    // Validate
    const validationResult = validateCoordinates(currentCoords)
    const error =
      validationResult !== ValidateCoordinatesEnum.Success ? `${validationResult}` : undefined

    setLastKnownLocations(prev => {
      const prevLocation = prev[0]
      let delta = 0

      // Calculate Delta
      if (
        currentCoords.latitude &&
        currentCoords.longitude &&
        prevLocation?.latitude &&
        prevLocation?.longitude
      ) {
        delta = Math.round(
          getPointDistance(
            { lat: currentCoords.latitude, lon: currentCoords.longitude },
            { lat: prevLocation.latitude, lon: prevLocation.longitude }
          )
        )
      }

      const newEntry: DiagnosticsRow = {
        ...currentCoords,
        delta,
        error,
      }

      return [newEntry, ...prev].slice(0, MAX_LOCATION_UPDATES)
    })

    setNumberOfUpdates(prev => ++prev)
    if (error) setNumberOfErrors(prev => ++prev)
    if (currentCoords.closestVenue?.inside) setNumberOfInside(prev => ++prev)
  }, [currentCoords])

  const displayableLocations = lastKnownLocations.slice(0, MAX_VISIBLE_LOCATION_UPDATES)
  const shownCount = Math.min(lastKnownLocations.length, MAX_VISIBLE_LOCATION_UPDATES)

  const exportData = () => {
    const defaultFilename = `venue_mode_diagnostics_${dayjs().format('YYYYMMDD_HHmmss')}.json`
    const outputFileName = prompt('Downloading\n\n\nSave as... or cancel saving', defaultFilename)
    if (outputFileName === null) return

    try {
      const data = { lastKnownLocations }
      const dataJson = JSON.stringify(
        data,
        (key, value) => (key == '_persist' ? undefined : value),
        2
      )
      if (isReactNativeApp) {
        sendToNative('SHARE_FILE_REQUEST', {
          data: dataJson,
          file: outputFileName,
          title: 'Sharing diagnostics',
          type: 'application/json',
        })
      } else {
        const blob = new Blob([dataJson], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = outputFileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
      }
    } catch (error) {
      alert(`Error exporting data\n\n${error}`)
    }
  }

  const clearData = () => {
    if (!confirm(`Are you sure you want to clear the page's data?`)) return
    setLastKnownLocations([])
  }

  return (
    <DiagnosticsContainerStyled>
      <OverviewStyled>
        <StatisticsStyled>
          <StatisticsRowStyled>
            Shown: {shownCount}, Total: {numberOfUpdates}, Errors: {numberOfErrors}, Inside:{' '}
            {numberOfInside}
          </StatisticsRowStyled>
          {!isReactNativeApp && (
            <StatisticsRowStyled>
              Web Permission 'Access Location':{' '}
              {currentCoords?.isEnabledHighAccuracy?.toString() ?? '-'}
            </StatisticsRowStyled>
          )}
          {isReactNativeApp && (
            <StatisticsRowStyled>
              App Permission 'Precision Accuracy':{' '}
              {currentCoords?.isEnabledHighAccuracy?.toString() ?? '-'}
            </StatisticsRowStyled>
          )}
        </StatisticsStyled>

        <ButtonContainerStyled>
          <VenueModeButton isFullWidth={false} onClick={exportData}>
            Export
          </VenueModeButton>
          <VenueModeButton isFullWidth={false} onClick={clearData}>
            Clear
          </VenueModeButton>
        </ButtonContainerStyled>
      </OverviewStyled>

      <TableStyled>
        <RowStyled backgroundShade='dark'>
          <CellIndexStyled>#</CellIndexStyled>
          <CellLatLonStyled>Latitude</CellLatLonStyled>
          <CellLatLonStyled>Longitude</CellLatLonStyled>
          <CellAccuracyStyled>Accuracy</CellAccuracyStyled>
          <CellDeltaStyled>∆</CellDeltaStyled>
          <CellMockedStyled>Mocked</CellMockedStyled>
          <CellPreciseAccuracyStyled>Precise</CellPreciseAccuracyStyled>
          <CellTimeStyled>Timestamp</CellTimeStyled>
          <CellInsideStyled>Inside</CellInsideStyled>
          <CellDistanceStyled>Distance</CellDistanceStyled>
          <CellVenueLocationStyled>VL</CellVenueLocationStyled>
          <CellVenueNameStyled>Name</CellVenueNameStyled>
          <CellErrorStyled>Error</CellErrorStyled>
        </RowStyled>
        {displayableLocations.map((location, index) => {
          const {
            latitude,
            longitude,
            accuracy,
            mocked,
            isEnabledHighAccuracy,
            timestamp,
            closestVenue,
            delta,
            error,
          } = location

          return (
            <RowStyled
              key={index}
              backgroundShade={
                error
                  ? 'error'
                  : closestVenue?.inside
                    ? 'inside'
                    : index % 2 == 0
                      ? 'light'
                      : 'dark'
              }
              onClick={() => error && alert(`Error for this record:\n\n${error}`)}
            >
              <CellIndexStyled>{index + 1}</CellIndexStyled>
              <CellLatLonStyled>{latitude}</CellLatLonStyled>
              <CellLatLonStyled>{longitude}</CellLatLonStyled>
              <CellAccuracyStyled>{prettifyDistance(accuracy)}</CellAccuracyStyled>
              <CellDeltaStyled> {prettifyDistance(delta)} </CellDeltaStyled>
              <CellMockedStyled>{mocked?.toString() ?? '-'}</CellMockedStyled>
              <CellPreciseAccuracyStyled>
                {isEnabledHighAccuracy?.toString() ?? '-'}
              </CellPreciseAccuracyStyled>
              <CellTimeStyled>
                {dayjs(timestamp).tz('Australia/Perth').format('HH:mm:ss.SSS')}
              </CellTimeStyled>
              <CellInsideStyled>{closestVenue?.inside ? 'yes' : 'no'}</CellInsideStyled>
              <CellDistanceStyled>{prettifyDistance(closestVenue?.distance)}</CellDistanceStyled>
              <CellVenueLocationStyled>
                {closestVenue?.venueId}
                {closestVenue?.locationId}
              </CellVenueLocationStyled>
              <CellVenueNameStyled>{closestVenue?.name}</CellVenueNameStyled>
              <CellErrorStyled>{error ?? '-'}</CellErrorStyled>
            </RowStyled>
          )
        })}
      </TableStyled>
    </DiagnosticsContainerStyled>
  )
}
